import React, { useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { Button } from '@ontruck/orc';
import { useTranslation } from 'react-i18next';
import { useOrder } from 'hooks/useOrders';
import { actions as uiActions } from 'state/ui';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import { useDispatch } from 'react-redux';
import { actions as orderActions } from 'state/order';
import { useFeasibility } from 'hooks/useFeasibility';
import {
  getIsCancellable,
  getIsEditable,
  getIsRepeatable,
} from 'utils/order/methods';
import { IS_TENANT } from 'tenant/utils';

const Actions = ({ className, orderId }) => {
  const { t } = useTranslation('ModalOrderDetail');
  const dispatch = useDispatch();
  const { order } = useOrder(orderId);
  const hasAppointment = !!order?.shipments?.[0]?.appointment;
  const { getIsOrderNonCore } = useFeasibility();
  const isOrderNonCore = useMemo(
    () => getIsOrderNonCore(order),
    [getIsOrderNonCore, order]
  );

  const isCancellable = getIsCancellable(order);
  const isRepeatable = !isOrderNonCore && getIsRepeatable(order);
  const isEditable = !isOrderNonCore && getIsEditable(order);

  const repeatOrder = useCallback(() => {
    dispatch(
      orderActions.repeatOrder({ originalOrder: order, place: 'DETAIL' })
    );
  }, [dispatch, order]);

  const editOrder = useCallback(() => {
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.shipperOrderEditClicked({
          place: 'DETAIL',
          orderId,
          status: order.status,
        })
      )
    );
    dispatch(orderActions.editOrder({ originalOrder: order }));
  }, [dispatch, order, orderId]);

  const cancelOrder = useCallback(() => {
    dispatch(uiActions.toggleCancelOrderModalOpened({ order }));
    dispatch(
      analyticsActions.sendEvent(
        analyticsEvents.shipperOrderCancelClicked({
          place: 'DETAIL',
          orderId,
        })
      )
    );
  }, [dispatch, order, orderId]);

  if (IS_TENANT) return <></>;

  return (
    <>
      <div className={className} data-testid="actions">
        <div className="wrapper">
          {isRepeatable && (
            <div className="repeat-btn">
              <Button
                data-testid="repeat-button"
                skin="secondary"
                icon="repeat"
                iconPosition="left"
                text={t('footer.repeat_button')}
                onClick={repeatOrder}
              />
            </div>
          )}

          {isEditable && !hasAppointment && (
            <div className="edit-btn">
              <Button
                data-testid="edit-button"
                icon="edit"
                iconPosition="left"
                text={t('Orders:edit.action')}
                skin="secondary"
                onClick={editOrder}
              />
            </div>
          )}

          {isCancellable && (
            <div className="cancel-btn">
              <Button
                data-testid="cancel-button"
                icon="trash"
                iconPosition="left"
                text={t('footer.cancel')}
                skin="secondary"
                onClick={cancelOrder}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

Actions.propTypes = {
  orderId: PropTypes.string.isRequired,
};

export default Actions;
